import { maybe, Maybe, RemoteData } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import type {
  ResourceTypeBindingsApi,
  ResourceTypeFieldAssignment,
  ResourceTypeWorkspaceBinding
} from 'v5/api/resource-types/resource-type-bindings.api';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { ResourceTypeBindingsService } from './ResourceTypeBindingsService';

const bindingsQueryKey = (organizationId: string) =>
  ['resource-type-bindings', organizationId] as const;

/**
 * Thin facade over {@link ResourceTypeBindingsApi} + React Query.
 * No custom pub/sub — same shape as other IO services.
 */
export function createResourceTypeBindingsService(config: {
  api: ResourceTypeBindingsApi;
  queryClient: QueryClient;
}): ResourceTypeBindingsService {
  const { api, queryClient } = config;

  async function invalidate(organizationId: string) {
    await queryClient.invalidateQueries({
      queryKey: bindingsQueryKey(organizationId)
    });
  }

  return {
    useBindings(
      organizationId: Maybe<string>
    ): RemoteData<ResourceTypeWorkspaceBinding[]> {
      return ensureIdleQuery(
        organizationId,
        useQuery({
          enabled: maybe.isPresent(organizationId),
          queryKey: bindingsQueryKey(organizationId ?? ''),
          queryFn: () => api.list(organizationId!)
        }) as RemoteData<ResourceTypeWorkspaceBinding[]>
      );
    },

    ensureBindings(organizationId) {
      return queryClient.fetchQuery({
        queryKey: bindingsQueryKey(organizationId),
        queryFn: () => api.list(organizationId)
      });
    },

    async getBinding(organizationId, resourceTypeId, workspaceId) {
      const bindings = await queryClient.fetchQuery({
        queryKey: bindingsQueryKey(organizationId),
        queryFn: () => api.list(organizationId)
      });
      return (
        bindings.find(
          binding =>
            binding.resourceTypeId === resourceTypeId &&
            binding.workspaceId === workspaceId
        ) ?? null
      );
    },

    async setEnabled(input) {
      const next = await api.setEnabled(input);
      await invalidate(input.organizationId);
      return next;
    },

    async setWorkspaceFields(input: {
      organizationId: string;
      resourceTypeId: number;
      workspaceId: number;
      workspaceFields: ResourceTypeFieldAssignment[];
    }) {
      const next = await api.setWorkspaceFields(input);
      await invalidate(input.organizationId);
      return next;
    }
  };
}
